import React from 'react';
import { useMemo } from 'react';
import { CommonLoader } from 'views/admin/Common/Loader';
import ReportPicker, { usePersistedReport } from 'views/admin/Common/ReportPicker';
import AttendanceReport from './AttendanceReport';
import FeePendingReport from './FeePendingReport';
import ReportTable from './ReportTable';
import RouteCards from './RouteCards';
import { buildRouteReport, buildBusReport, buildDriverReport } from './transportReports';

const REPORTS = [
  {
    key: 'attendance',
    label: 'BUS ATTENDANCE',
    icon: 'fas fa-clipboard-check',
    description: 'Pickup, drop and absent - by day, week or month',
  },
  { key: 'routes', label: 'ROUTES', icon: 'fas fa-route', description: 'Stops, students, bus and driver, capacity' },
  { key: 'buses', label: 'BUSES & COMPLIANCE', icon: 'fas fa-bus', description: 'Fleet status and expiring documents' },
  { key: 'drivers', label: 'DRIVERS', icon: 'fas fa-id-card', description: 'Drivers with their bus and route' },
  {
    key: 'fees',
    label: 'FEE PENDING',
    icon: 'fas fa-rupee-sign',
    description: 'Who has paid the bus fee and who owes it, by route',
  },
];

// The Transport reports: pick a report, get summary tiles + table + Excel export. The route / bus / driver reports
// are built from the Transport data handed in (the same data the Transport page shows); attendance is read from the
// backend's bus-attendance reports for the chosen period.
const TransportReportsView = props => {
  const {
    isLoading,
    batchId,
    routes,
    areasByRoute,
    studentsByRoute,
    assignments,
    buses,
    drivers,
    routesById,
    busesById,
    driversById,
  } = props;
  const [report, setReport] = usePersistedReport('transportReport', REPORTS, 'attendance');

  const routeReport = useMemo(
    () => buildRouteReport({ routes, areasByRoute, studentsByRoute, assignments, busesById, driversById }),
    [routes, areasByRoute, studentsByRoute, assignments, busesById, driversById]
  );
  const busReport = useMemo(
    () => buildBusReport({ buses, assignments, routesById, driversById }),
    [buses, assignments, routesById, driversById]
  );
  const driverReport = useMemo(
    () => buildDriverReport({ drivers, assignments, busesById, routesById }),
    [drivers, assignments, busesById, routesById]
  );

  if (!batchId) {
    return (
      <div style={{ textAlign: 'center', color: '#9a9a9a', padding: 40 }}>
        Transport reports belong to an academic year. Please select a batch first.
      </div>
    );
  }
  if (isLoading) return <CommonLoader />;

  return (
    <>
      <ReportPicker items={REPORTS} value={report} onChange={setReport} />
      {report === 'attendance' && <AttendanceReport {...props} />}
      {report === 'routes' && (
        <ReportTable
          title="Routes"
          fileName="transport_routes"
          emptyText="No routes yet."
          renderRows={rows => <RouteCards routes={rows} />}
          {...routeReport}
        />
      )}
      {report === 'buses' && (
        <ReportTable title="Buses" fileName="transport_buses" emptyText="No buses yet." {...busReport} />
      )}
      {report === 'fees' && <FeePendingReport {...props} />}
      {report === 'drivers' && (
        <ReportTable title="Drivers" fileName="transport_drivers" emptyText="No drivers yet." {...driverReport} />
      )}
    </>
  );
};

export default TransportReportsView;
